import classNames from 'classnames';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import type { Op } from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import ScrollArea from '../../components/ScrollArea';
import Toast from '../../components/Toast';
import { CREATION_ID } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { Comment_CommentFragment } from '../../graphql/generated/graphql';
import { UIActions } from '../../ui/uiSlice';
import HoverContentPreview from '../HoverContentPreview';
import Comment from './Comment';
import CommentCreate from './CommentCreate';
import { ANIMATED_COMMENT_PREVIEW_DURATION } from './CommentPreview';
import * as styles from './CommentThreadFull.module.scss';
import FoldedComments from './FoldedComments';
import handleUnresolveThreadError from './handleUnresolveThreadError';
import useCommentActions from './useCommentActions';

const COMMENT_LIMIT = 2;

const POST_FRAGMENT = gql(`
  fragment CommentThreadFull_post on RootPostFields {
    id
    ...Comment_post
    ...CommentCreate_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentThreadFull_thread on PublicThreadFields {
    id
    comments {
      id
      ...Comment_comment
    }
    ...CommentCreate_thread
  }
`);

export interface CommentThreadFullProps {
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  isExpanded?: boolean;
  hovered?: boolean;
  initialShowingMore?: boolean;
  onCreated?: () => void;
  onResize?: () => void;
  unsavedInputValue?: Op[];
  onInputChange?: (content: Op[]) => void;
  unresolveThread: (threadId: string) => Promise<unknown>;
  wide?: boolean;
}

const CommentThreadFull = ({
  thread,
  post,
  isExpanded = false,
  hovered,
  initialShowingMore = false,
  onCreated,
  onResize = () => {},
  unsavedInputValue = [],
  onInputChange,
  unresolveThread,
  wide,
}: CommentThreadFullProps) => {
  const { id: threadId, comments } = thread;

  const dispatch = useAppDispatch();

  const { resolveThread, focusCommentThread } = useCommentActions(
    thread.id,
    post,
  );

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const threadCommentsRef = useRef<OverlayScrollbarsComponentRef>(null);

  const isCreating = threadId === CREATION_ID;

  const isShowingThread = !isCreating && comments && comments.length > 0;

  const handleCreated = () => {
    onCreated?.();

    setTimeout(() => {
      const viewport = threadCommentsRef.current
        ?.osInstance()
        ?.elements().viewport;
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }, ANIMATED_COMMENT_PREVIEW_DURATION * 1000);
  };

  useEffect(() => {
    onResize();

    if (!isExpanded) {
      setEditingCommentId(null);
    }
  }, [isExpanded, onResize]);

  const handleResolve = async () => {
    await resolveThread();

    dispatch(
      UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
        <Toast
          level="success"
          onDismiss={toastProps.onDismiss}
          message="Comment resolved"
          secondaryText="Undo"
          onSecondaryClick={() => {
            toastProps.onDismiss();
            unresolveThread(thread.id).catch((e) =>
              handleUnresolveThreadError(e, dispatch),
            );
          }}
        />
      )),
    );
  };

  const onEdit = (commentId: string) => {
    setEditingCommentId(commentId);
    focusCommentThread();
    onResize();
  };

  const inner = (
    <FoldedComments<Comment_CommentFragment>
      comments={comments}
      limit={isExpanded ? Infinity : COMMENT_LIMIT}
      isExpanded={isExpanded}
      renderComment={(comment, idx, isFolded) => {
        const isFirst = idx === 0;
        const isLast = idx === comments.length - 1;

        return (
          <Comment
            key={comment.id}
            expanded={isExpanded}
            first={isFirst}
            last={isLast}
            initialShowingMore={initialShowingMore}
            onResize={onResize}
            comment={comment}
            threadId={threadId}
            post={post}
            onResolve={handleResolve}
            editing={editingCommentId === comment.id}
            onEdit={() => {
              onEdit(comment.id);
            }}
            onCancelEdit={() => {
              setEditingCommentId(null);
              onResize();
            }}
            folded={isFolded}
            disabledAnimatedPreview={wide}
            threadHovered={!!hovered}
          />
        );
      }}
    />
  );

  return (
    <div
      className={classNames('comment-thread-full', { expanded: isExpanded })}
    >
      {isShowingThread && (
        <ScrollArea
          ref={threadCommentsRef}
          events={{
            scroll: (event) => {
              const { viewport } = event.elements();
              setIsAtBottom(
                Math.ceil(viewport.scrollTop + viewport.clientHeight) >=
                  viewport.scrollHeight,
              );
            },
            updated: (event) => {
              setHasOverflow(event.state().hasOverflow.y);
            },
          }}
        >
          {isExpanded ? (
            inner
          ) : (
            <HoverContentPreview.DisableHoverBoundary>
              {inner}
            </HoverContentPreview.DisableHoverBoundary>
          )}
        </ScrollArea>
      )}
      {isExpanded && (
        <div
          className={classNames('comment-thread-create', styles.create, {
            [styles.hasMoreScrollContent]: hasOverflow && !isAtBottom,
          })}
        >
          <CommentCreate
            post={post}
            thread={thread}
            onChange={onInputChange}
            onCreated={handleCreated}
            onResize={onResize}
            initialContent={unsavedInputValue}
            disabled={editingCommentId !== null}
          />
        </div>
      )}
    </div>
  );
};

export default CommentThreadFull;
